/**
 * Cliente do rail público de navegação do funil v2 (Argus): /flow/next.
 *
 * É o elo que faltava na RECUSA: o rail respondia recusa corretamente desde o
 * início (coberto pelo harness do Argus), mas nenhum cliente em produção a
 * enviava — o Bolt só resolve o aceite pós-compra, e o Midas era 100% v1.
 * Funil v2 com downsell morria aqui: quem clicava "não, obrigado" no upsell
 * não tinha quem o levasse adiante.
 *
 * Mesma filosofia do cliente do Bolt: timeout curto e null em QUALQUER falha.
 * A navegação de recusa nunca pode prender o comprador numa tela — sem
 * resposta, o chamador segue para o fallback (página de obrigado).
 */

export interface FlowNextStep {
	node_id: string | null;
	node_type: string | null;
	url: string | null;
	offer_code: string | null;
	is_thankyou: boolean;
	discount: {
		type: string;
		value: string;
		title?: string;
		description?: string;
		coupon?: { name: string } | null;
	} | null;
}

interface ResolveParams {
	apiKey: string;
	offerCode: string;
	decision: 'accept' | 'reject';
	orderHash?: string | null;
	/**
	 * Desambigua o node de partida quando o MESMO offer_code vive em mais de
	 * um node — o desenho clássico é o desconto de recuperação linkando a
	 * mesma oferta do upsell. Sem ele, o segundo salto da recusa resolvia de
	 * volta para o upsell e virava loop. Toda resposta traz node_id; o salto
	 * seguinte o devolve.
	 */
	nodeId?: string | null;
	/** Estabiliza o braço de teste A/B no servidor (hash por jornada). */
	journeyId?: string | null;
}

/**
 * Jornada do comprador, vinda do `_ticjrn` da URL — é como a identidade
 * atravessa domínios (o LinkDecorator do SDK decora os links com ela).
 *
 * MESMA lição da condicional por cliente: identidade que se perde no meio do
 * funil muda o resultado sem ninguém perceber. Sem jornada, recusa que
 * atravessa um teste A/B (A/B de downsell) cai SEMPRE no braço de controle —
 * o split nunca acontece no pós-compra. Ausência degrada o teste, nunca a
 * navegação.
 */
export function resolveJourneyId(): string | undefined {
	try {
		return new URLSearchParams(window.location.search).get('_ticjrn') ?? undefined;
	} catch {
		return undefined;
	}
}

const TIMEOUT_MS = 2500;

export async function resolveNextFlowStep({
	apiKey,
	offerCode,
	decision,
	orderHash,
	nodeId,
	journeyId,
}: ResolveParams): Promise<FlowNextStep | null> {
	const baseUrl = process.env.REACT_APP_ARGUS_API_URL;

	if (!baseUrl || !apiKey || !offerCode) {
		return null;
	}

	const params = new URLSearchParams({ offer_code: offerCode, decision });
	if (orderHash) params.set('order_hash', orderHash);
	if (nodeId) params.set('node_id', nodeId);
	if (journeyId) params.set('journey_id', journeyId);

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

	try {
		const response = await fetch(`${baseUrl.replace(/\/$/, '')}/flow/next?${params.toString()}`, {
			headers: { 'X-API-Key': apiKey },
			signal: controller.signal,
		});

		if (!response.ok) {
			return null;
		}

		const body = await response.json();

		return (body?.next as FlowNextStep) ?? null;
	} catch {
		return null;
	} finally {
		clearTimeout(timer);
	}
}
